import React from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api } from "@/lib/api";
import { ArrowUpRight, Sparkle, MegaphoneSimple, Funnel, TrendUp } from "@phosphor-icons/react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";

const fetcher = (u) => api.get(u).then(r => r.data);

const Card = ({ children, className = "", ...rest }) => (
  <div className={`border border-white/[0.08] bg-[#0a0a0a] rounded-sm p-5 ${className}`} {...rest}>{children}</div>
);

const Stat = ({ label, value, delta, testId }) => (
  <Card data-testid={testId}>
    <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">{label}</div>
    <div className="mt-3 font-heading font-black text-4xl tracking-tighter">{value}</div>
    {delta && <div className="mt-2 text-[11px] font-mono text-success">{delta}</div>}
  </Card>
);

export default function Dashboard() {
  const { data: summary } = useSWR("/analytics/summary", fetcher);
  const { data: leads } = useSWR("/leads", fetcher);
  const { data: perf } = useSWR("/agents/copywriter/performance", fetcher);

  const platformData = summary?.by_platform
    ? Object.entries(summary.by_platform).map(([k, v]) => ({ platform: k.replace("_", " "), leads: v }))
    : [];

  const sectorData = summary?.by_sector
    ? Object.entries(summary.by_sector).map(([k, v]) => ({ sector: k, leads: v }))
    : [];

  const perfData = (perf || []).slice(0, 12).map((p, i) => ({
    idx: i + 1, ctr: p.ctr * 100, conv: p.conversions,
  }));

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground">Console · Overview</div>
          <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Command Center</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/app/agent-a" data-testid="quick-agent-a" className="inline-flex items-center gap-2 border border-white/10 px-4 py-2 rounded-sm text-sm hover-lift hover:border-primary/40"><Sparkle size={14} className="text-primary" /> Run Agent A</Link>
          <Link to="/app/agent-b" data-testid="quick-agent-b" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-sm text-sm font-medium primary-glow hover-lift"><MegaphoneSimple size={14} /> Draft ads</Link>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testId="stat-leads" label="Social leads captured" value={summary?.leads_total ?? 0} delta={`${((summary?.sync_rate ?? 0) * 100).toFixed(1)}% synced`} />
        <Stat testId="stat-impressions" label="Ad impressions" value={(summary?.ad_totals?.impressions ?? 0).toLocaleString()} />
        <Stat testId="stat-ctr" label="Avg CTR" value={`${((summary?.ad_totals?.avg_ctr ?? 0) * 100).toFixed(2)}%`} />
        <Stat testId="stat-spend" label="Ad spend" value={`$${(summary?.ad_totals?.spend ?? 0).toLocaleString()}`} delta={`${summary?.ad_totals?.conversions ?? 0} conversions`} />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">Leads by source platform</div>
              <div className="mt-1 font-heading font-bold text-lg">Attribution split</div>
            </div>
            <TrendUp size={18} className="text-primary" />
          </div>
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={platformData}>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="platform" tick={{ fill: "#8F8F8F", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#1a1a1a" }} tickLine={false} />
                <YAxis tick={{ fill: "#8F8F8F", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#1a1a1a" }} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 2, fontSize: 12 }} cursor={{ fill: "rgba(226,184,89,0.08)" }} />
                <Bar dataKey="leads" fill="#E2B859" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">Hook sector</div>
          <div className="mt-1 font-heading font-bold text-lg">Loan mix</div>
          <div className="mt-6 space-y-3">
            {sectorData.length === 0 && <div className="text-xs text-muted-foreground">No data yet.</div>}
            {sectorData.map((s) => (
              <div key={s.sector}>
                <div className="flex justify-between text-xs font-mono">
                  <span>{s.sector}</span><span className="text-primary">{s.leads}</span>
                </div>
                <div className="h-1 bg-white/[0.05] mt-1">
                  <div className="h-1 bg-primary" style={{ width: `${Math.min(100, (s.leads / Math.max(...sectorData.map(x => x.leads))) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">Ad variant CTR (%) · rolling</div>
              <div className="mt-1 font-heading font-bold text-lg">A/B micro-test performance</div>
            </div>
            <Link to="/app/agent-b" className="text-xs text-primary inline-flex items-center gap-1 hover:opacity-80">Open Ad Studio <ArrowUpRight size={12} /></Link>
          </div>
          <div className="mt-6 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={perfData}>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="idx" tick={{ fill: "#8F8F8F", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#1a1a1a" }} tickLine={false} />
                <YAxis tick={{ fill: "#8F8F8F", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#1a1a1a" }} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 2, fontSize: 12 }} />
                <Line type="monotone" dataKey="ctr" stroke="#E2B859" strokeWidth={2} dot={{ fill: "#E2B859", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">Recent captures</div>
              <div className="mt-1 font-heading font-bold text-lg">Live pipeline</div>
            </div>
            <Link to="/app/leads" data-testid="view-all-leads" className="text-xs text-primary inline-flex items-center gap-1"><Funnel size={12} /> View all</Link>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left py-2 pr-4">Lead ID</th>
                  <th className="text-left py-2 pr-4">Platform</th>
                  <th className="text-left py-2 pr-4">Sector</th>
                  <th className="text-left py-2 pr-4">Intent</th>
                  <th className="text-left py-2 pr-4">Engagement</th>
                  <th className="text-left py-2">Handoff</th>
                </tr>
              </thead>
              <tbody>
                {(!leads || leads.length === 0) && (
                  <tr><td colSpan={6} className="py-6 text-muted-foreground text-center">No leads captured yet.</td></tr>
                )}
                {(leads || []).slice(0, 6).map((l) => (
                  <tr key={l.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                    <td className="py-2 pr-4">{l.social_lead_id}</td>
                    <td className="py-2 pr-4">{l.marketing_attribution.sourcePlatform}</td>
                    <td className="py-2 pr-4 text-primary">{l.marketing_attribution.hookSector}</td>
                    <td className="py-2 pr-4">{l.pre_sort_metrics.selfReportedIntent}</td>
                    <td className="py-2 pr-4">{(l.pre_sort_metrics.initialChatEngagementScore * 100).toFixed(0)}%</td>
                    <td className="py-2">{l.handoff_state.mainSiteSyncComplete ? <span className="text-success">SYNCED</span> : <span className="text-muted-foreground">PENDING</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
